import type {
    Conditions,
    DataSourceAbstract,
    DateRangeVariableValue,
    FieldADTValue,
    FieldType,
    FilterBlockAbstract,
    FilterCommonCondition,
    FilterConditions,
    FilterFormType,
    FilterGroupType,
    FilterVariable,
    PageStackBlockValueBlockMap,
    ResolvedVariableConfig,
    VariableADTvalue,
    VariableFieldADTValue
} from '@lighthouse/core'
import { DEVICE_ID_VALUE, FilterWay, ImageVariableValue, VariableType } from '@lighthouse/core'
import { find } from 'rambda'

import { DEFAULT_RESOLVED_VALUE_VARIABLE, FILTER_OPERATOR } from '../../components'
import { defaultFormat, InnerTypeMapByFieldType, innerTypeToFieldTypeMapInCondition, InputTypeToFieldTypeMap } from '../../constants'
import type { FlowNodeResultADT, ParseVariableOptions, Workflow } from '../../types'
import { getWithScopeId } from '../block'
import { getBlockValueFromBlockMap } from '../blockValue'
import { getRealFieldByInnerType } from '../field'
import { getUpstreamRealDsId } from '../flow'
import { getDeviceId, getWindowUrl } from '../helper'
import { getArgsResult, getRecordResult } from './helper/common'
import { generateDataRangeByRangeValue } from './helper/dateRangeGenerator'

export type ParseVariable2FieldOption = ParseVariableOptions

export type ResolvedFilterExtraParams = Partial<
    ParseVariable2FieldOption & {
        dataSourceList: DataSourceAbstract[]
        workflow: Workflow
        pageStackFormState: PageStackBlockValueBlockMap
        parseVariable2Field: (value: VariableADTvalue, option?: ParseVariable2FieldOption) => FieldADTValue | undefined
        upstreamNodesResult?: Record<string, FlowNodeResultADT>
    }
>

type TransformToValueVariablePayload = {
    variable: VariableADTvalue
    extraParams?: ResolvedFilterExtraParams
}

type ResolveFilterItemParams = {
    condition: FilterCommonCondition
    extraParams?: ResolvedFilterExtraParams
    useInFilterRecordChecker?: boolean
}

type ResolveFilterToValueParams = {
    where: 'OR' | 'AND'
    conditions: Conditions
    extraParams?: ResolvedFilterExtraParams
    useInFilterRecordChecker?: boolean
}

type ResolveFilterParams = {
    filter?: FilterFormType
    extraParams?: ResolvedFilterExtraParams
    useInFilterRecordChecker?: boolean
}

interface ConvertFilterBlockConditionsParams {
    filterCondition: FilterCommonCondition
    filterVariable: FilterVariable['filterVariable']
    // filterBlocks: FilterBlockAbstract[]
    // filterValue: {
    //     blockId: string
    //     data: FilterBlockValue
    // }[]
    // filterOptions: Record<string, FilterOption[]>
    parseVariable2Field: (value: VariableADTvalue, option?: ParseVariable2FieldOption) => FieldADTValue | undefined
}

interface AssembleFilterConditionsParams {
    filterCondition?: FilterCommonCondition
    idVariable: VariableADTvalue
    paramList: VariableADTvalue[]
    filterBlocks: FilterBlockAbstract[]
    parseVariable2Field: (value: VariableADTvalue, option?: ParseVariable2FieldOption) => FieldADTValue | undefined
    upstreamNodesResult?: Record<string, FlowNodeResultADT>
    scope?: string
}

interface AssembleFilterTypeConditionsParams {
    // filterCondition: FilterCommonCondition
    idVariable: VariableADTvalue
    param: VariableADTvalue
    parseVariable2Field: (value: VariableADTvalue, option?: ParseVariable2FieldOption) => FieldADTValue | undefined
    scope?: string
}

export const validateFilterFieldData = (filter: FilterFormType, dataSource: DataSourceAbstract): FilterFormType => {
    const { expression } = filter
    const { schema } = dataSource
    if (!expression || !expression.conditions) {
        return filter
    }
    const newConditions = expression.conditions.filter(condition => {
        const idVariable = (condition as FilterCommonCondition)?.idVariable
        if (!idVariable) {
            return false
        }
        if (idVariable.type === VariableType.FIELD_ID) {
            if (!idVariable.fieldIdVariable?.fieldId) {
                return false
            }
            const field = schema[idVariable.fieldIdVariable.fieldId]
            if (!field) {
                return false
            }
        }
        return true
    })

    return {
        ...filter,
        expression: {
            ...expression,
            conditions: newConditions
        }
    }
}

/**
 * 根据筛选控制器选项筛选数据，生成筛选器筛选条件（被包含关系）
 * 文本，统一转为 = 条件，并用 OR 连接
 * 数值，统一转为区间判断，使用 >= 和 <= 条件，项内用 AND 连接，项间使用 OR 链接
 * 日期，统一日期区间，使用 >= 和 <= 条件，项内用 AND 连接，项间使用 OR 链接
 * 人员，统一转为 contains 条件，并用 OR 连接
 * 选项，统一转为 contains 条件，并用 OR 连接
 */
/**
 * 处理文本筛选
 * @param params
 * @returns
 */
const assembleTextFilterConditions = (params: AssembleFilterTypeConditionsParams): FilterCommonCondition[] | undefined => {
    const { idVariable, param, parseVariable2Field, scope } = params

    const fieldValue = parseVariable2Field(param, { scope })
    const value = fieldValue?.value
    if (!value || !Array.isArray(value)) {
        return
    }
    return value.map(val => {
        return {
            idVariable,
            operator: 'contains',
            paramList: [
                {
                    type: VariableType.VALUE,
                    valueVariable: {
                        type: 'text',
                        value: String(val)
                    }
                }
            ]
        }
    })
}
/**
 * 处理数字筛选
 * @param params
 * @returns
 */
const assembleNumberFilterConditions = (params: AssembleFilterTypeConditionsParams): FilterGroupType[] | undefined => {
    const { idVariable, param, parseVariable2Field, scope } = params
    const fieldValue = parseVariable2Field(param, { scope })
    const value = fieldValue?.value
    if (!value || !Array.isArray(value)) {
        return
    }

    return value?.map(val => {
        if (!val || typeof val === 'boolean' || typeof val === 'number') {
            return undefined
        }
        const numberRange = val.split(',')
        // 对应于 value 的 [min, max]
        const operators = ['>=', '<=']
        return {
            where: 'AND',
            conditions: operators
                .map(
                    (operator, i) =>
                        !!numberRange[i] && {
                            idVariable,
                            operator,
                            paramList: [
                                {
                                    type: VariableType.VALUE,
                                    valueVariable: {
                                        type: 'number',
                                        value: Number(numberRange[i])
                                    }
                                }
                            ]
                        }
                )
                .filter(Boolean)
        }
    }) as FilterGroupType[]
}
/**
 * 处理日期筛选
 * @param params
 * @returns
 */
const assembleDateFilterConditions = (params: AssembleFilterTypeConditionsParams): FilterGroupType[] | undefined => {
    const { idVariable, param, parseVariable2Field, scope } = params
    const fieldValue = parseVariable2Field(param, { scope })
    const value = fieldValue?.value
    if (!value || !Array.isArray(value)) {
        return
    }
    // 选中数据
    // const values = filterItemData?.value
    const realValues = value
        ?.map(val => {
            if (!val || typeof val !== 'string') {
                return undefined
            }
            if (val.includes(',')) {
                return val.split(',').map(Number)
            }
            return generateDataRangeByRangeValue(val as DateRangeVariableValue)
        })
        .filter(Boolean)

    return realValues?.map(value => {
        if (!value) {
            return undefined
        }
        // 对应于 value 的 [min, max]
        const operators = ['>=', '<=']
        return {
            where: 'AND',
            conditions: operators
                .map((operator, i) => {
                    const dateValue = value[i]
                    return (
                        dateValue && {
                            idVariable,
                            operator,
                            paramList: [
                                {
                                    type: VariableType.VALUE,
                                    valueVariable: {
                                        type: 'date',
                                        value: dateValue.valueOf()
                                    }
                                }
                            ]
                        }
                    )
                })
                .filter(Boolean)
        }
    }) as FilterGroupType[]
}
/**
 * 处理人员筛选
 * @param params
 * @returns
 */
const assemblePersonFilterConditions = (params: AssembleFilterTypeConditionsParams): FilterCommonCondition[] | undefined => {
    const { idVariable, param, parseVariable2Field, scope } = params
    const fieldValue = parseVariable2Field(param, { scope })
    const value = fieldValue?.value
    if (!value || !Array.isArray(value)) {
        return
    }

    return value?.map(val => {
        return {
            idVariable,
            operator: 'contains',
            paramList: [
                {
                    type: VariableType.VALUE,
                    valueVariable: {
                        type: 'text',
                        value: String(val)
                    }
                }
            ]
        }
    })
}
/**
 * 处理选项筛选
 * @param params
 * @returns
 */
const assembleSelectFilterConditions = (params: AssembleFilterTypeConditionsParams): FilterCommonCondition[] | undefined => {
    const { idVariable, param, parseVariable2Field, scope } = params
    const fieldValue = parseVariable2Field(param, { scope })
    const value = fieldValue?.value
    if (!value || !Array.isArray(value)) {
        return
    }

    return value?.map(val => {
        return {
            idVariable,
            operator: 'contains',
            paramList: [
                {
                    type: VariableType.VALUE,
                    valueVariable: {
                        type: 'text',
                        value: val
                    }
                }
            ]
        }
    }) as FilterCommonCondition[] | undefined
}

/**
 * 处理布尔筛选
 * @param params
 * @returns
 */
const assembleBoolFilterConditions = (params: AssembleFilterTypeConditionsParams): FilterCommonCondition[] | undefined => {
    const { idVariable, param, parseVariable2Field, scope } = params
    const fieldValue = parseVariable2Field(param, { scope })
    const value = fieldValue?.value
    if (value === undefined) {
        return
    }
    const boolValue = String(value) === 'true' ? true : String(value) === 'false' ? false : undefined
    if (boolValue === undefined) {
        return
    }
    return [
        {
            idVariable,
            operator: '=',
            paramList: [
                {
                    type: VariableType.VALUE,
                    valueVariable: {
                        type: 'checkbox',
                        value: boolValue
                    }
                }
            ]
        }
    ]
}

const assembleFilterConditions = (params: AssembleFilterConditionsParams): FilterConditions[] | undefined => {
    const { idVariable, paramList, filterBlocks, parseVariable2Field, scope } = params
    const param = paramList?.[0]
    if (!param) {
        return
    }
    if (param.type !== VariableType.FILTER) {
        return
    }
    const { filterVariable } = param
    if (!filterVariable) {
        return
    }
    const { blockId, itemId } = filterVariable
    const filterBlock = find(item => item.id === blockId, filterBlocks)
    if (!filterBlock) {
        return
    }
    const filterItem = find(item => item.id === itemId, filterBlock.config.filterItems)

    const payload = {
        idVariable,
        param,
        parseVariable2Field,
        scope
    }

    switch (filterItem?.filterWay) {
        case FilterWay.textFilter: {
            return assembleTextFilterConditions(payload)
        }
        case FilterWay.numberFilter: {
            return assembleNumberFilterConditions(payload)
        }
        case FilterWay.dateFilter: {
            return assembleDateFilterConditions(payload)
        }
        case FilterWay.personFilter: {
            return assemblePersonFilterConditions(payload)
        }
        case FilterWay.selectFilter: {
            return assembleSelectFilterConditions(payload)
        }
        case FilterWay.cascadeFilter: {
            return assembleTextFilterConditions(payload)
        }
        case FilterWay.boolFilter: {
            return assembleBoolFilterConditions(payload)
        }
        default: {
            return []
        }
    }
}

const transformToValueVariable = (payload: TransformToValueVariablePayload): VariableADTvalue | undefined => {
    const { variable, extraParams } = payload
    const { type } = variable
    const dataSourceList = extraParams?.dataSourceList

    switch (type) {
        case VariableType.UPSTREAM: {
            const { upstreamVariable } = variable

            if (!upstreamVariable || !upstreamVariable.nodeId || !upstreamVariable.fieldId) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
                // newParamList.push(param)
                // continue
            }

            const { nodeId, fieldId } = upstreamVariable

            const record = getRecordResult(extraParams?.upstreamNodesResult?.[nodeId])

            const fieldValue = record?.content?.[fieldId]?.value

            const dsId = getUpstreamRealDsId(nodeId, extraParams?.workflow?.content?.nodes || [])
            const dataSource = dataSourceList?.find(({ id }) => id === dsId)
            const field = dataSource?.schema[fieldId]

            if (!field) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }
            const realFiled = getRealFieldByInnerType(field, innerTypeToFieldTypeMapInCondition)
            if (!realFiled) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }

            if (realFiled.type === 'date') {
                return {
                    type: VariableType.RESOLVED,
                    resolvedVariable: {
                        type: realFiled.type,
                        value: fieldValue,
                        format: realFiled.date.format || defaultFormat,
                        innerType: realFiled.innerType
                    } as ResolvedVariableConfig['resolvedVariable']
                }
            }
            return {
                type: VariableType.RESOLVED,
                resolvedVariable: {
                    type: realFiled.type,
                    value: fieldValue,
                    innerType: realFiled.innerType
                } as ResolvedVariableConfig['resolvedVariable']
            }
        }
        case VariableType.ARG_VARIABLE: {
            const { argVariable } = variable

            if (!argVariable || !argVariable.nodeId || !argVariable.argId) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }

            const { nodeId, argId } = argVariable

            const args = getArgsResult(extraParams?.upstreamNodesResult?.[nodeId])
            const arg = args?.find(item => item.id === argId)

            if (!arg) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }
            return {
                type: VariableType.RESOLVED,
                resolvedVariable: {
                    value: arg.value,
                    innerType: arg.innerType
                } as ResolvedVariableConfig['resolvedVariable']
            }
        }
        case VariableType.VALUE: {
            const { valueVariable } = variable

            if (!valueVariable) {
                return variable
            }

            const { type, value } = valueVariable

            const newValue = value

            if (type === 'checkbox') {
                // 过滤掉空值
                return {
                    type: VariableType.VALUE,
                    valueVariable: { type, value: !!newValue } as VariableFieldADTValue // 这里要过滤
                }
            }
            return variable
        }
        case VariableType.INPUT: {
            // // TODO: 张琪留下的坑 atom还没更新就调用了解析方法 使用的参数中的pageStackFormState来进行解析
            // const pageStackFormState = extraParams?.pageStackFormState
            // if (pageStackFormState) {
            //     const viewRecordId = extraParams?.viewRecord?.id
            //     const { inputVariable } = variable
            //     const { blockId = '', scope } = inputVariable ?? {}
            //     const blockKey = getWithScopeId(blockId, scope)
            //     const formState = getBlockValueFromBlockMap(pageStackFormState, {
            //         blockId: blockKey,
            //         recordId: viewRecordId
            //     })
            //     if (!formState || (formState?.scope && formState?.scope !== scope)) {
            //         return DEFAULT_RESOLVED_VALUE_VARIABLE
            //     }
            //     if (formState.blockType === 'field') {
            //         const fieldType: FieldType = formState.fieldType || InputTypeToFieldTypeMap[formState.type]
            //         const innerType = InnerTypeMapByFieldType[fieldType]
            //         return {
            //             type: VariableType.RESOLVED,
            //             resolvedVariable: { type: fieldType, value: formState.value, innerType } as ResolvedVariableConfig['resolvedVariable'] // 这里要过滤
            //             // valueVariable: { type: fieldType, value: formState.value } as VariableFieldADTValue // 这里要过滤
            //         }
            //     }
            //     if (formState.blockType === 'text') {
            //         return {
            //             type: VariableType.RESOLVED,
            //             resolvedVariable: { type: 'text', value: formState.value, innerType: 'TEXT' } as ResolvedVariableConfig['resolvedVariable']
            //         }
            //     }
            //     if (formState.blockType === 'onOff') {
            //         return {
            //             type: VariableType.RESOLVED,
            //             resolvedVariable: { type: 'checkbox', value: formState.value, innerType: 'BOOL' } as ResolvedVariableConfig['resolvedVariable']
            //         }
            //     }
            //     return DEFAULT_RESOLVED_VALUE_VARIABLE
            // }

            const fieldValue = extraParams?.parseVariable2Field?.(variable, {
                formRecord: extraParams.formRecord,
                viewRecord: extraParams.viewRecord,
                imageInfo: extraParams.imageInfo,
                scope: extraParams.scope
            })

            if (!fieldValue) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }

            return {
                type: VariableType.RESOLVED,
                resolvedVariable: fieldValue
            }
        }
        case VariableType.FORM: {
            const { formVariable } = variable
            if (!formVariable || !formVariable.fieldId) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }

            const fieldValue = extraParams?.parseVariable2Field?.(variable, {
                formRecord: extraParams.formRecord,
                viewRecord: extraParams.viewRecord,
                imageInfo: extraParams.imageInfo,
                scope: extraParams.scope
            })
            if (!fieldValue) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }
            return {
                type: VariableType.RESOLVED,
                resolvedVariable: fieldValue
            }
        }
        case VariableType.FILTER: {
            const { filterVariable } = variable
            if (!filterVariable || !filterVariable.blockId || !filterVariable.itemId) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }

            const value = extraParams?.parseVariable2Field?.(variable, { scope: extraParams.scope })
            if (!value) {
                return {
                    type: VariableType.VALUE,
                    valueVariable: value
                }
            }
            return {
                type: VariableType.RESOLVED,
                resolvedVariable: value as ResolvedVariableConfig['resolvedVariable']
            }
        }
        case VariableType.USER:
        case VariableType.SYSTEM: {
            return variable
        }
        case VariableType.ROUTE_PATH: {
            const { routePathVariable } = variable
            if (!routePathVariable) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }

            const value = extraParams?.parseVariable2Field?.(variable, { scope: extraParams.scope })

            return {
                type: VariableType.RESOLVED,
                resolvedVariable: value as ResolvedVariableConfig['resolvedVariable']
            }
        }
        case VariableType.ROUTE_QUERY: {
            const { routeQueryVariable } = variable
            if (!routeQueryVariable) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }

            const value = extraParams?.parseVariable2Field?.(variable, { scope: extraParams.scope })

            return {
                type: VariableType.RESOLVED,
                resolvedVariable: value as ResolvedVariableConfig['resolvedVariable']
            }
        }
        case VariableType.PAGE_STATE: {
            const { pageStateVariable } = variable
            if (!pageStateVariable || !pageStateVariable.id) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }
            if (!extraParams?.parseVariable2Field) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }
            const field = extraParams?.parseVariable2Field?.(variable, { scope: extraParams.scope })
            return {
                type: VariableType.RESOLVED,
                resolvedVariable: {
                    type: field?.type,
                    value: field?.value,
                    innerType: field?.innerType
                } as ResolvedVariableConfig['resolvedVariable']
            }
        }
        case VariableType.PAGE_DATA_QUERY: {
            const { pageDataQueryVariable } = variable

            if (!pageDataQueryVariable || !pageDataQueryVariable.id || !pageDataQueryVariable.itemId) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }

            if (!extraParams?.parseVariable2Field) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }
            const field = extraParams?.parseVariable2Field?.(variable, { scope: extraParams.scope })
            return {
                type: VariableType.RESOLVED,
                resolvedVariable: {
                    type: field?.type,
                    value: field?.value,
                    innerType: field?.innerType
                } as ResolvedVariableConfig['resolvedVariable']
            }
        }
        case VariableType.VIEW: {
            const { viewVariable } = variable

            if (!viewVariable || !viewVariable.fieldId) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }

            const { fieldId } = viewVariable

            const viewRecord = extraParams?.viewRecord
            if (!viewRecord) {
                const FieldAdtValue = extraParams?.parseVariable2Field?.(variable, { scope: extraParams.scope })
                return {
                    type: VariableType.RESOLVED,
                    resolvedVariable: {
                        type: FieldAdtValue?.type,
                        value: FieldAdtValue?.value,
                        innerType: FieldAdtValue?.innerType
                    } as ResolvedVariableConfig['resolvedVariable']
                }
            }
            const fieldValue = viewRecord.content?.[fieldId]?.value
            const dsId = viewRecord?.dsId
            const dataSource = dataSourceList?.find(({ id }) => id === dsId)
            const field = dataSource?.schema[fieldId]

            if (!field) {
                const FieldAdtValue = extraParams?.parseVariable2Field?.(variable, { scope: extraParams.scope, viewRecord })
                if (!FieldAdtValue) {
                    return DEFAULT_RESOLVED_VALUE_VARIABLE
                }
                return {
                    type: VariableType.RESOLVED,
                    resolvedVariable: {
                        type: FieldAdtValue.type,
                        value: fieldValue ?? FieldAdtValue.value,
                        innerType: FieldAdtValue.innerType
                    } as ResolvedVariableConfig['resolvedVariable']
                }
            }
            return {
                type: VariableType.RESOLVED,
                resolvedVariable: {
                    type: field?.type,
                    value: fieldValue,
                    innerType: field.innerType
                } as ResolvedVariableConfig['resolvedVariable']
            }
        }
        case VariableType.PAGE_LINK: {
            const { pageLinkVariable } = variable
            if (pageLinkVariable?.value === 'CURRENT_PAGE') {
                const link = getWindowUrl()
                return {
                    type: VariableType.RESOLVED,
                    resolvedVariable: { type: 'url', value: link, innerType: 'TEXT' } as ResolvedVariableConfig['resolvedVariable']
                }
            }
            return DEFAULT_RESOLVED_VALUE_VARIABLE
        }
        case VariableType.DEVICE_ID: {
            if (variable.deviceIdVariable?.value === DEVICE_ID_VALUE) {
                return {
                    type: VariableType.RESOLVED,
                    resolvedVariable: {
                        type: 'text',
                        value: getDeviceId(),
                        innerType: 'TEXT'
                    } as ResolvedVariableConfig['resolvedVariable']
                }
            }
            return DEFAULT_RESOLVED_VALUE_VARIABLE
        }
        case VariableType.IMAGE: {
            const { imageVariable } = variable
            if (!imageVariable || !imageVariable.dataKey) {
                return DEFAULT_RESOLVED_VALUE_VARIABLE
            }
            const dataKey = imageVariable.dataKey
            const data = extraParams?.imageInfo?.[dataKey]
            const innerType = dataKey === ImageVariableValue.IMAGE_INDEX ? 'NUMBER' : 'TEXT'
            return {
                type: VariableType.RESOLVED,
                resolvedVariable: { type: 'url', value: data, innerType } as ResolvedVariableConfig['resolvedVariable']
            }
        }
        default: {
            return variable
        }
    }
}

/**
 * 解析过滤器相关变量
 * 主要使用在动作、动作流、筛选器中，将过滤器中的变量转换为真实数据
 * @param ResolveFilterParams
 * @returns
 */
const resolveFilterItem = (payload: ResolveFilterItemParams) => {
    const { condition, extraParams, useInFilterRecordChecker } = payload

    const { paramList, idVariable, operator: conditionOperator } = condition

    if (!idVariable || !paramList) {
        return condition
    }
    const operator = conditionOperator
    const newParamList: VariableADTvalue[] = []

    const newIdVariable = transformToValueVariable({
        variable: idVariable,
        extraParams
    })
    if (newIdVariable === undefined) {
        return
    }
    for (const param of paramList) {
        const { type } = param

        /**
         * 如果是筛选器校验，直接返回
         */
        if (useInFilterRecordChecker && type !== VariableType.UPSTREAM && type !== VariableType.ARG_VARIABLE) {
            newParamList.push(param)
            continue
        }

        const newParam = transformToValueVariable({ variable: param, extraParams })
        if (newParam === undefined) {
            return
        }
        newParamList.push(newParam)
    }

    if (operator !== FILTER_OPERATOR && newParamList?.length) {
        return {
            ...condition,
            idVariable: newIdVariable,
            operator,
            paramList: newParamList
        }
    }

    return condition
}

const resolveExpression = ({ where, conditions, extraParams, useInFilterRecordChecker }: ResolveFilterToValueParams): FilterGroupType => {
    const newConditions = conditions
        ?.map(condition => {
            if (condition.where && condition.conditions) {
                return resolveExpression({
                    where: condition.where,
                    conditions: condition.conditions,
                    extraParams,
                    useInFilterRecordChecker
                })
            }
            return resolveFilterItem({ condition, extraParams, useInFilterRecordChecker })
        })
        ?.filter(Boolean) as Conditions

    return {
        where,
        conditions: newConditions
    }
}

export const resolveFilter = ({ filter, extraParams, useInFilterRecordChecker }: ResolveFilterParams): FilterFormType | undefined => {
    if (!filter) {
        return filter
    }
    const { expression } = filter

    if (!expression || !expression.conditions || !expression.where) {
        return filter
    }
    const { where, conditions } = expression
    const newExpression = resolveExpression({ where, conditions, extraParams, useInFilterRecordChecker })
    return {
        expression: newExpression
    }
}

/** 递归提取变量列表 */
export const recTakeFilterVariable = (expression: FilterGroupType | undefined) => {
    if (!expression || !expression.conditions || !expression.where) {
        return
    }

    const { conditions } = expression

    const res = conditions.reduce<VariableADTvalue[]>((total, condition) => {
        if (condition.where && condition.conditions) {
            const innerRes = recTakeFilterVariable(condition)
            if (innerRes) {
                total.push(...innerRes)
            }

            return total
        }

        const filterCondition = condition as FilterCommonCondition
        if (filterCondition.idVariable) {
            total.push(filterCondition.idVariable)
        }
        if (filterCondition.paramList) {
            total.push(...filterCondition.paramList)
        }
        return total
    }, [])

    if (res.length === 0) {
        return
    }

    return res
}

interface FilterBlockParams {
    parseVariable2Field: (value: VariableADTvalue, option?: ParseVariable2FieldOption) => FieldADTValue | undefined
    scope?: string
    // filterValue: {
    //     blockId: string
    //     data: FilterBlockValue
    // }[]
    // filterOptions: Record<string, FilterOption[]>
    // filterBlocks: FilterBlockAbstract[]
}

export const resolveFilter4Block = ({
    filter,
    filterBlocks,
    filterBlockParams
}: {
    filter: FilterFormType
    filterBlocks: FilterBlockAbstract[]
    filterBlockParams: FilterBlockParams
}) => {
    if (!filter) {
        return filter
    }
    const { expression } = filter

    if (!expression || !expression.conditions || !expression.where) {
        return filter
    }

    const resolveFilterItem4Block = ({
        condition,
        filterBlockParams
    }: {
        condition: FilterCommonCondition
        filterBlockParams: FilterBlockParams
    }) => {
        const { idVariable, paramList } = condition

        if (!idVariable || !paramList) {
            return condition
        }

        const conditions =
            assembleFilterConditions({
                idVariable,
                filterBlocks,
                paramList,
                parseVariable2Field: filterBlockParams.parseVariable2Field,
                scope: filterBlockParams.scope
            }) || []

        return {
            where: 'OR',
            conditions
        }
    }

    const resolveExpression4Block = ({
        expression,
        filterBlockParams
    }: {
        expression: FilterGroupType
        filterBlockParams: FilterBlockParams
    }): FilterGroupType => {
        const newConditions = expression.conditions
            ?.map(condition => {
                if (condition.where && condition.conditions) {
                    return resolveExpression4Block({
                        expression: condition,
                        filterBlockParams
                    })
                }
                return resolveFilterItem4Block({ condition, filterBlockParams })
            })
            ?.filter(Boolean) as Conditions

        return {
            where: expression.where,
            conditions: newConditions
        }
    }

    const newExpression = resolveExpression4Block({ expression, filterBlockParams })

    return {
        expression: newExpression
    }
}
